import React, { useEffect, useRef, useState } from "react";
import Card from "components/card";

import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { IoEye } from "react-icons/io5";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import UserService from "services/user.service";
import Loader from "../../../assets/img/loader.gif";
import Toast from "components/toaster";
import DateRangePicker from "components/fields/DateRangePicker";
import moment from "moment";
import { FiSearch, FiCheck, FiX } from "react-icons/fi";
import AccountService from "services/accounts.service";
import VerificationBadgeService from "services/verificationbadge.service";
import CardMenu from "components/card/CardMenuDropdown";
import Modal from "./components/Modal";
import Select from "react-select";
import {
  AiOutlineCheckCircle,
  AiOutlineCloseCircle,
  AiOutlineFieldTime,
} from "react-icons/ai";
import { BsChevronDown } from "react-icons/bs";
import { MdCheck, MdClose, MdEdit } from "react-icons/md";
import GooglePagination from "components/pagination/GooglePagination";

const columnHelper = createColumnHelper();

// Module-level tabCache for persistent data across component mounts/unmounts
const tabCache = {
  "account-verification": {
    data: null,
    firstLoad: true,
  },
};

const AccountVerification = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const dropdownRef = useRef();
  const verifyBadgeDropdownRef = useRef();
  const profileRef = useRef();
  const activateRef = useRef();
  const [sorting, setSorting] = React.useState([]);
  // Initialize with cached data if available to prevent "No data found" flash
  const [data, setData] = useState(() => {
    const cache = tabCache["account-verification"];
    return cache?.data?.data || [];
  });
  const [isLoading, setIsLoading] = useState(() => {
    const cache = tabCache["account-verification"];
    // Only show loading if no cache exists
    return cache?.firstLoad && !cache?.data;
  });
  const [popupOpen, setPopupOpen] = useState(false);
  const [profilePopup, setProfilePopup] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [activeVerifyBadgeDropdown, setActiveVerifyBadgeDropdown] = useState(null);
  const [isViewLoading, setIsViewLoading] = useState(false);
  const [id, setId] = useState("");
  const [confirmLoader, setConfirmLoader] = useState(false);
  
  // Initialize date filter from query parameters
  const [value, setValue] = useState(() => {
    const startDateParam = searchParams.get("startDate");
    const endDateParam = searchParams.get("endDate");
    return {
      startDate: startDateParam ? new Date(startDateParam) : null,
      endDate: endDateParam ? new Date(endDateParam) : null,
    };
  });
  const [searchText, setSearchText] = useState("");
  const [selectAccountType, setSelectAccountType] = useState("");
  const [selectBadge, setSelectbadge] = useState("");
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  // Initialize pagination with cached data if available
  const [totalPages, setTotalPages] = useState(() => {
    const cache = tabCache["account-verification"];
    if (cache?.data?.totalCount) {
      return Math.ceil(cache.data.totalCount / 20);
    }
    return 1;
  });
  const [totalCount, setTotalCount] = useState(() => {
    const cache = tabCache["account-verification"];
    return cache?.data?.totalCount || 0;
  });
  
  // Coin balance editing state
  const [editingCoinRowId, setEditingCoinRowId] = useState(null);
  const [editingCoinValue, setEditingCoinValue] = useState("");
  const [originalCoinValue, setOriginalCoinValue] = useState("");

  const getUser = async (showLoader = true) => {
    if (showLoader) {
    setIsLoading(true);
    }
    try {
      const body = {
        pagination: {
          page: currentPage,
          pageSize: pageSize,
          projection: {},
          filters: {
            startDate: value.startDate
              ? moment(value.startDate).startOf("day").format()
              : null,
            endDate: value.endDate
              ? moment(value.endDate).endOf("day").format()
              : null,
            ...((selectAccountType || selectAccountType?.value !== null) && {
              account_type: selectAccountType?.value || selectAccountType,
            }),
          },
          sort: {},
        },
        searchtext: searchText,
      };
      const response = await AccountService.getAccount(body);
      const responseData = response.data.data;
      const responseTotalCount = response.data.totalCount || 0;
      
      // Update cache
      tabCache["account-verification"].data = {
        data: responseData,
        totalCount: responseTotalCount,
      };
      
      // Set firstLoad to false after successful fetch
      if (tabCache["account-verification"].firstLoad) {
        tabCache["account-verification"].firstLoad = false;
      }
      
      setData(responseData);
      setTotalCount(responseTotalCount);
      setTotalPages(Math.ceil(responseTotalCount / pageSize));
    } catch (error) {
      Toast("error", error.response?.data?.message || "Failed to fetch accounts");
    } finally {
      if (showLoader) {
      setIsLoading(false);
      }
    }
  };
  
  useEffect(() => {
    // Check cache first
    const cache = tabCache["account-verification"];
    
    // If cached data exists, show it instantly
    if (cache.data) {
      setData(cache.data.data || []);
      setTotalCount(cache.data.totalCount || 0);
      setTotalPages(Math.ceil((cache.data.totalCount || 0) / pageSize));
    }
    
    // Always fetch fresh data in background
    // Show loader only on firstLoad (when no cache exists yet)
    const shouldShowLoader = cache.firstLoad && !cache.data;
    getUser(shouldShowLoader);
  }, [
    currentPage,
    pageSize,
    searchText,
    value.startDate,
    value.endDate,
    selectAccountType,
    selectBadge,
  ]);

  // Listen for badge approval events to refresh the table
  useEffect(() => {
    const handleBadgeApproval = () => {
      // Refresh the account table when a verification badge is approved
      getUser(false);
    };

    window.addEventListener('verificationBadgeApproved', handleBadgeApproval);
    
    return () => {
      window.removeEventListener('verificationBadgeApproved', handleBadgeApproval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // getUser uses current state values, so we don't need it in deps

  // Update query parameters when date filter changes (but not on initial mount from URL)
  useEffect(() => {
    const currentStartDate = searchParams.get("startDate");
    const currentEndDate = searchParams.get("endDate");
    const newStartDate = value.startDate ? moment(value.startDate).format("YYYY-MM-DD") : null;
    const newEndDate = value.endDate ? moment(value.endDate).format("YYYY-MM-DD") : null;
    
    // Only update if the values actually changed (avoid updating on initial mount)
    if (currentStartDate !== newStartDate || currentEndDate !== newEndDate) {
      const params = new URLSearchParams(searchParams);
      if (newStartDate) {
        params.set("startDate", newStartDate);
      } else {
        params.delete("startDate");
      }
      if (newEndDate) {
        params.set("endDate", newEndDate);
      } else {
        params.delete("endDate");
      }
      setSearchParams(params, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value.startDate, value.endDate]);

  const handleSearch = (e) => {
    setSearchText(e.target.value);
    setCurrentPage(1); // Reset to first page when searching
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    window.scrollTo(0, 0); // Scroll to top when changing pages
  };

  useEffect(() => {
    const type = localStorage.getItem("type");
    if (type) {
      if (type !== "verified") {
        setSelectAccountType(type);
      }
      if (type == "page" || type == "verified") {
        setSelectAccountType("page");
        setSelectbadge("verified");
      }
    }
    localStorage.removeItem("type");
  }, []);

  const handleOpen = async (item) => {
    setIsViewLoading(true);
    setId(item);
    try {
      const body = {
        id: item,
      };
      const response = await UserService.getUserDetails(body);
      setProfilePopup(true);
      document.body.style.overflow = "hidden";
    } catch (error) {
      Toast("error", error.response.data.message);
    } finally {
      setIsViewLoading(false);
      setId("");
    }
  };

  const options = [
    { label: "All", value: null },
    { label: "Page", value: "page" },
    { label: "Personal", value: "personal" },
    { label: "Community", value: "community" },
  ];
  const VeryfiedOption = [
    { label: "All", value: 1 },
    { label: "Verified", value: 2 },
    { label: "Unverified", value: 3 },
  ];

  const handleOpenUser = async (id, data) => {
    setSelectedUser(data?.original?.is_active);
    setId(id);
    setPopupOpen(true);
    document.body.style.overflow = "hidden";
  };

  const handleSelectAccountType = async (option) => {
    setSelectAccountType(option);
    setCurrentPage(1); // Reset to first page when filtering
  };
  
  const handleSelectbadgeType = async (option) => {
    setSelectbadge(option?.value);
    setCurrentPage(1); // Reset to first page when filtering
  };

  const handleStatusChange = async (rowId, newStatus) => {
    setIsViewLoading(true);
    try {
      const body = {
        account_id: rowId,
        is_active: newStatus,
      };
      const response = await AccountService.updateAccount(body);
      Toast("success", response.data.message);
      getUser(false);
    } catch (error) {
      Toast(
        "error",
        error.response?.data?.message || "Failed to update status"
      );
    } finally {
      setActiveDropdown(null);
      setIsViewLoading(false);
    }
  };

  const handleVerifyBadgeChange = async (rowId, newStatus) => {
    setIsViewLoading(true);
    try {
      const body = {
        account_id: rowId,
        assign: newStatus,
      };
      const response = await VerificationBadgeService.manageVerificationBadge(body);
      
      // Optimistically update the local state immediately
      setData(prevData => 
        prevData.map(item => {
          if (item._id === rowId) {
            return {
              ...item,
              is_verified: newStatus,
              VerifyBadge: newStatus, // Also update VerifyBadge field if it exists
            };
          }
          return item;
        })
      );
      
      Toast("success", response.data.message || "Verification badge updated successfully");
      
      // Refresh data in background to ensure consistency
      getUser(false);
    } catch (error) {
      Toast(
        "error",
        error.response?.data?.message || "Failed to update verification badge"
      );
      // Refresh data on error to revert any optimistic updates
      getUser(false);
    } finally {
      setActiveVerifyBadgeDropdown(null);
      setIsViewLoading(false);
    }
  };

  const handleToggleActivation = async () => {
    setConfirmLoader(true);
    try {
      const body = {
        account_id: id,
        is_active: !selectedUser,
      };
      const response = await AccountService.updateAccount(body);
      setPopupOpen(false);
      document.body.style.overflow = "unset";
      Toast("success", response.data.message);
      getUser(false);
    } catch (error) {
      Toast("error", error.response.data.message);
    } finally {
      setConfirmLoader(false);
    }
  };

  const handleEditCoinBalance = (rowId, currentValue) => {
    setEditingCoinRowId(rowId);
    setEditingCoinValue(currentValue.toString());
    setOriginalCoinValue(currentValue.toString());
  };

  const handleSaveCoinBalance = async (rowId, accountId) => {
    try {
      const newValue = parseFloat(editingCoinValue);
      if (isNaN(newValue) || newValue < 0) {
        return;
      }

      // Try different possible field name combinations
      // The API might expect: coins, coin_balance, balance, or coinBalance
      // Also try with integer value if API expects whole numbers
      const body = {
        account_id: accountId,
        coins: Math.round(newValue), // Try 'coins' with integer value
      };

      console.log("Updating coin balance with body:", body);
      const response = await AccountService.updateCoinBalance(body);
      setEditingCoinRowId(null);
      setEditingCoinValue("");
      setOriginalCoinValue("");
      getUser(false); // Refresh the data
    } catch (error) {
      console.error("Error updating coin balance:", error);
      console.error("Error response:", error.response);
      console.error("Error response data:", error.response?.data);
      console.error("Full error:", JSON.stringify(error.response?.data, null, 2));
    }
  };

  const handleCancelCoinBalance = () => {
    setEditingCoinRowId(null);
    setEditingCoinValue("");
    setOriginalCoinValue("");
  };

  const handleCoinBalanceChange = (e) => {
    const value = e.target.value;
    
    // Allow empty input for deletion
    if (value === "") {
      setEditingCoinValue("");
      return;
    }
    
    // Remove any non-numeric characters except decimal point
    const numericValue = value.replace(/[^\d.]/g, "");
    
    // Check if the number of digits (excluding decimal point) exceeds 6
    const digitsOnly = numericValue.replace(/\./g, "");
    if (digitsOnly.length > 6) {
      Toast("error", "Coin balance cannot exceed 6 digits");
      return; // Don't update if more than 6 digits
    }
    
    setEditingCoinValue(numericValue);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setProfilePopup(false);
        document.body.style.overflow = "unset";
      }
      if (activateRef.current && !activateRef.current.contains(event.target)) {
        setPopupOpen(false);
        document.body.style.overflow = "unset";
      }
      // Close status dropdown when clicking outside
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        const isStatusTrigger = event.target.closest('[data-dropdown="status"]');
        if (!isStatusTrigger) {
          setActiveDropdown(null);
        }
      }
      // Close verify badge dropdown when clicking outside
      if (verifyBadgeDropdownRef.current && !verifyBadgeDropdownRef.current.contains(event.target)) {
        const isVerifyBadgeTrigger = event.target.closest('[data-dropdown="verify-badge"]');
        if (!isVerifyBadgeTrigger) {
          setActiveVerifyBadgeDropdown(null);
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [activeDropdown, activeVerifyBadgeDropdown]);

  const columns = [
    columnHelper.accessor("full_name", {
      id: "full_name",
      header: () => (
        <p className="text-sm font-bold uppercase text-white">Full Name</p>
      ),
      cell: (info) => (
        <p className="text-sm font-bold text-white">
          {info.row.original.name ? info.row.original.name : info.row.original.username ? info.row.original.username : "-"}
        </p>
      ),
    }),
    columnHelper.accessor("account_type", {
      id: "account_type",
      header: () => (
        <p className="text-sm font-bold uppercase text-white">Account Type</p>
      ),
      cell: (info) => (
        <p className="text-sm font-bold text-white">
          {info.row.original.account_type
            ? info.row.original.account_type
            : "-"}
        </p>
      ),
    }),
    columnHelper.accessor("is_active", {
      id: "is_active",
      header: () => (
        <p className="text-sm font-bold uppercase text-white">Active</p>
      ),
      cell: (info) => (
        <div className="flex items-center">
          {info.getValue() ? "Yes" : "No"}
        </div>
      ),
    }),
    columnHelper.accessor("email", {
      id: "email",
      header: () => (
        <p className="text-sm font-bold uppercase text-white">Email</p>
      ),
      cell: (info) => (
        <div className="flex items-center">
          {info.getValue() ? info.getValue() : "--"}
        </div>
      ),
    }),
    columnHelper.accessor("coin_balance", {
      id: "coin_balance",
      header: () => (
        <p className="text-sm font-bold uppercase text-white">Coin Balance</p>
      ),
      cell: (info) => {
        const rowId = info.row.original._id;
        const coinBalanceData = info.row.original.coin_balance || 
                               info.row.original.coins || 
                               info.row.original.balance || 
                               info.row.original.coinBalance;
        
        // Handle case where coin_balance is an object with a balance property
        let coinBalance = 0;
        if (coinBalanceData) {
          if (typeof coinBalanceData === 'object' && coinBalanceData !== null) {
            coinBalance = coinBalanceData.balance || coinBalanceData.coin_balance || 0;
          } else {
            coinBalance = coinBalanceData;
          }
        }
        
        const isEditing = editingCoinRowId === rowId;
        const displayValue = coinBalance !== undefined && coinBalance !== null ? coinBalance : 0;
        
        if (isEditing) {
          return (
            <div className="flex items-center gap-2">
              <input
                type="number"
                min="0"
                step="0.01"
                max="999999"
                value={editingCoinValue}
                onChange={handleCoinBalanceChange}
                className="w-16 rounded-md border border-gray-600 bg-navy-800 px-2 py-1 text-sm font-bold text-white focus:border-blue-500 focus:outline-none"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleSaveCoinBalance(rowId, info.row.original._id);
                  } else if (e.key === 'Escape') {
                    handleCancelCoinBalance();
                  }
                }}
              />
              <button
                onClick={() => handleSaveCoinBalance(rowId, info.row.original._id)}
                className="flex items-center justify-center rounded-md bg-green-500 p-1.5 text-white hover:bg-green-600"
                title="Save"
              >
                <FiCheck className="h-4 w-4 " />
              </button>
              <button
                onClick={handleCancelCoinBalance}
                className="flex items-center justify-center rounded-md bg-red-500 p-1.5 text-white hover:bg-red-600"
                title="Cancel"
              >
                <FiX className="h-4 w-4"  />
              </button>
            </div>
          );
        }
        
        return (
          <div className="flex items-center gap-2">
            <p className="text-sm font-bold text-white">
              {displayValue}
            </p>
            <button
              onClick={() => handleEditCoinBalance(rowId, displayValue)}
              className="flex items-center justify-center rounded-md p-1 text-orange-500 hover:text-orange-400"
              title="Edit coin balance"
            >
              <MdEdit className="h-3.5 w-3.5" />
            </button>
          </div>
        );
      },
    }),
    columnHelper.accessor("is_active", {
      id: "is_active",
      header: () => (
        <p className="text-sm font-bold uppercase -ml-[68%] text-white">Action</p>
      ),
      cell: (info) => {
        const rowId = info.row.original._id;
        const status = info.row.original?.is_active;
        // Check multiple possible field names for verification status
        const isVerified = info.row.original?.is_verified || 
                         info.row.original?.VerifyBadge || 
                         info.row.original?.verifyBadge ||
                         false;
        const badgeType = isVerified ? "Premium" : "Free";

        return (
          <div className="flex items-center gap-2 -ml-24">
            {/* Status Dropdown */}
            <div className="relative">
              <div
                data-dropdown="status"
                className={`ms-center m-0 flex w-28 cursor-pointer items-center justify-between rounded-lg ${
                  status ? "bg-green-500/20" : ""
                } px-3 py-1.5 text-sm font-bold ${
                  status ? "text-green-500" : ""
                }`}
                style={!status ? {
                  backgroundColor: "rgba(255,37,37,0.1)",
                  color: "#f06670d5"
                } : {}}
                onClick={() =>
                  setActiveDropdown(activeDropdown === rowId ? null : rowId)
                }
              >
                <span>{!status ? "In Active" : "Active"}</span>
                <BsChevronDown 
                  className={`h-3 w-3 ${status ? "text-green-500" : ""}`}
                  style={!status ? {
                    stroke: "#f06670d5",
                    strokeWidth: "1",
                    fill: "none"
                  } : {
                    stroke: "currentColor",
                    strokeWidth: "1",
                    fill: "none"
                  }}
                />
              </div>
              {activeDropdown === rowId && (
                <div
                  className="absolute left-0 z-50 mt-1 w-28 rounded-md bg-[#0F1735] border border-gray-600 px-1 py-1 shadow-lg"
                  ref={dropdownRef}
                >
                  {status ? (
                    // If Active, show only In Active option
                    <button
                      className="block w-full rounded-md px-2.5 py-1 text-left text-sm font-bold text-white transition-colors hover:bg-gray-600"
                      onClick={() => {
                        handleStatusChange(rowId, false);
                      }}
                    >
                      In Active
                    </button>
                  ) : (
                    // If In Active, show only Active option
                    <button
                      className="block w-full rounded-md px-2.5 py-1 text-left text-sm font-bold text-white transition-colors hover:bg-gray-600"
                      onClick={() => {
                        handleStatusChange(rowId, true);
                      }}
                    >
                      Active
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Verify Badge Dropdown */}
            <div className="relative">
              <div
                data-dropdown="verify-badge"
                className={`ms-center m-0 flex w-28 cursor-pointer items-center justify-between rounded-lg px-3 py-1.5 text-sm font-bold ${
                  isVerified 
                    ? "bg-[#FF8000]/10 text-[rgba(255,128,0,1)]" 
                    : "bg-[#369FFF]/10 text-[#369FFF]"
                }`}
                onClick={() =>
                  setActiveVerifyBadgeDropdown(activeVerifyBadgeDropdown === rowId ? null : rowId)
                }
              >
                <span>{badgeType}</span>
                <BsChevronDown 
                  className={`h-3 w-3 ${
                    isVerified 
                      ? "text-[rgba(255,128,0,1)]" 
                      : "text-[#369FFF]"
                  }`}
                  style={{
                    stroke: "currentColor",
                    strokeWidth: "1",
                    fill: "none"
                  }}
                />
              </div>
              {activeVerifyBadgeDropdown === rowId && (
                <div
                  className="absolute left-0 z-50 mt-1 w-28 rounded-md bg-[#0F1735] border border-gray-600 px-1 py-1 shadow-lg"
                  ref={verifyBadgeDropdownRef}
                >
                  {isVerified ? (
                    // If Premium, show only Free option
                    <button
                      className="block w-full rounded-md px-2.5 py-1 text-left text-sm font-bold text-white transition-colors hover:bg-gray-600"
                      onClick={() => {
                        handleVerifyBadgeChange(rowId, false);
                      }}
                    >
                      Free
                    </button>
                  ) : (
                    // If Free, show only Premium option
                    <button
                      className="block w-full rounded-md px-2.5 py-1 text-left text-sm font-bold text-white transition-colors hover:bg-gray-600"
                      onClick={() => {
                        handleVerifyBadgeChange(rowId, true);
                      }}
                    >
                      Premium
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        );
      },
    }),
  ];

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    debugTable: true,
  });

  const colourStyles = {
    control: (styles) => {
      return {
        ...styles,
        minHeight: "40px", // Match search button height (py-2 = 8px top + 8px bottom + content height)
        height: "40px",
      };
    },
    option: (styles, { data, isDisabled, isFocused, isSelected }) => {
      return {
        ...styles,
        backgroundColor: isDisabled
          ? undefined
          : isSelected
            ? "#24388a"
            : "#0b1437",
        color: isDisabled ? "#ccc" : "white",
        cursor: isDisabled ? "not-allowed" : "default",
        ":active": {
          ...styles[":active"],
          backgroundColor: !isDisabled
            ? isSelected
              ? "#24388a"
              : "#1a2d6b"
            : undefined,
        },
        ":hover": {
          backgroundColor: isSelected ? "#24388a" : "#1a2d6b",
          color: "white",
        },
      };
    },
  };

  return (
    <div className="mt-3 grid h-full grid-cols-1">
      <style>{`
        /* Very small screens - everything stacks vertically */
        @media (max-width: 424px) {
          .account-search-dropdown-container {
            flex-direction: column !important;
            align-items: flex-start !important;
          }
          .account-search-container {
            width: 100% !important;
            justify-content: flex-start !important;
            margin-top: 0 !important;
            margin-bottom: 1px !important;
          }
          .account-search-input-wrapper {
            width: 100% !important;
            max-width: 100% !important;
          }
          .account-search-input-wrapper input {
            width: 100% !important;
            max-width: 100% !important;
          }
          .account-right-container-small {
            flex-direction: column !important;
            width: 100% !important;
            align-items: flex-start !important;
            margin-top: 0 !important;
            margin-bottom: 10px !important;
            gap: 12px !important;
          }
          .account-mobile-dropdowns-small {
            width: 100% !important;
            flex-direction: column !important;
            align-items: flex-start !important;
            gap: 12px !important;
          }
          .account-mobile-dropdowns-small .select__control {
            width: 100% !important;
          }
          .account-date-picker-small {
            width: 100% !important;
            margin-top: 0 !important;
          }
        }
        
        /* Medium screens (425px - 768px) - search on own line, dropdowns and datepicker in one line */
        @media (min-width: 425px) and (max-width: 767px) {
          .account-search-dropdown-container {
            flex-direction: column !important;
            align-items: flex-start !important;
          }
          .account-search-container {
            width: 100% !important;
            justify-content: flex-start !important;
            margin-top: 0 !important;
            margin-bottom: 1px !important;
          }
          .account-search-input-wrapper {
            width: 100% !important;
            max-width: 100% !important;
          }
          .account-search-input-wrapper input {
            width: 100% !important;
            max-width: 100% !important;
          }
          .account-right-container-small {
            flex-direction: row !important;
            width: 100% !important;
            align-items: center !important;
            justify-content: flex-start !important;
            margin-top: 0 !important;
            margin-bottom: 10px !important;
            gap: 12px !important;
            flex-wrap: wrap !important;
          }
          .account-mobile-dropdowns-small {
            flex-direction: row !important;
            align-items: center !important;
            gap: 12px !important;
            width: auto !important;
          }
          .account-mobile-dropdowns-small .select__control {
            width: 150px !important;
          }
          .account-date-picker-small {
            width: auto !important;
            margin-top: 0 !important;
          }
        }
      `}</style>
      {/* Container wrapper */}
      <div className="col-span-1 w-full xl:col-span-1 min-w-0">
        <div className="flex justify-between gap-3 mb-4 account-search-dropdown-container">
          <div className="mt-2 md:mt-4 flex h-fit justify-start account-search-container">
            <div className="flex items-center rounded-full bg-navy-800 py-2 h-[40px] text-white xl:w-[170px] account-search-input-wrapper">
              <p className="pl-3 pr-2 text-xl">
                <FiSearch className="h-4 w-4 text-white" />
              </p>
              <input
                type="text"
                placeholder="Search..."
                className="block w-[90px] bg-navy-800 text-sm font-medium text-white outline-none placeholder:!text-white account-search-input"
                onChange={handleSearch}
              />
            </div>
          </div>
          <div className={`mt-2 md:mt-4 flex h-fit justify-end items-center gap-3 account-right-container-small ${
            selectAccountType?.value == "page" || selectAccountType == "page"
              ? "w-[580px]"
              : "w-[430px]"
          }`}>
            <Select
              className="hidden md:block w-[180px] !text-white"
              classNamePrefix="select"
              defaultValue={options[0]}
              name="color"
              options={options}
              value={options.find(
                (option) => {
                  const currentValue = selectAccountType?.value ?? selectAccountType;
                  return option?.value == currentValue || (option?.value === null && (currentValue === "" || currentValue === null));
                }
              )}
              getOptionValue={(option) => option.value}
              getOptionLabel={(option) => option.label}
              isOptionSelected={(option) => {
                const currentValue = selectAccountType?.value ?? selectAccountType;
                return option?.value == currentValue || (option?.value === null && (currentValue === "" || currentValue === null));
              }}
              onChange={handleSelectAccountType}
              styles={colourStyles}
            />

            {selectAccountType?.value == "page" ||
            selectAccountType == "page" ? (
              <Select
                className="hidden md:block w-[160px] !text-white"
                classNamePrefix="select"
                defaultValue={VeryfiedOption[0]}
                name="color"
                options={VeryfiedOption}
                value={VeryfiedOption.find(
                  (option) => {
                    const currentValue = selectBadge?.value ?? selectBadge;
                    return option?.value == currentValue || (option?.value == 1 && (currentValue === "" || currentValue === null || currentValue === undefined));
                  }
                )}
                getOptionValue={(option) => option.value}
                getOptionLabel={(option) => option.label}
                isOptionSelected={(option) => {
                  const currentValue = selectBadge?.value ?? selectBadge;
                  return option?.value == currentValue || (option?.value == 1 && (currentValue === "" || currentValue === null || currentValue === undefined));
                }}
                onChange={handleSelectbadgeType}
                styles={colourStyles}
              />
            ) : (
              ""
            )}
            <div className="flex items-center gap-3 md:hidden account-mobile-dropdowns-small">
              <Select
                className="w-[180px] !text-white"
                classNamePrefix="select"
                defaultValue={options[0]}
                name="color"
                options={options}
                value={options.find(
                  (option) => {
                    const currentValue = selectAccountType?.value ?? selectAccountType;
                    return option?.value == currentValue || (option?.value === null && (currentValue === "" || currentValue === null));
                  }
                )}
                getOptionValue={(option) => option.value}
                getOptionLabel={(option) => option.label}
                isOptionSelected={(option) => {
                  const currentValue = selectAccountType?.value ?? selectAccountType;
                  return option?.value == currentValue || (option?.value === null && (currentValue === "" || currentValue === null));
                }}
                onChange={handleSelectAccountType}
                styles={colourStyles}
              />
              {selectAccountType?.value == "page" ||
              selectAccountType == "page" ? (
                <Select
                  className="w-[200px] !text-white"
                  classNamePrefix="select"
                  defaultValue={VeryfiedOption[0]}
                  name="color"
                  options={VeryfiedOption}
                  value={VeryfiedOption.find(
                    (option) => option?.value == selectBadge
                  )}
                  getOptionValue={(option) => option.value}
                  getOptionLabel={(option) => option.label}
                  isOptionSelected={(option) => option?.value == selectBadge}
                  onChange={handleSelectbadgeType}
                  styles={colourStyles}
                />
              ) : (
                ""
              )}
            </div>
            <div className="flex items-center account-date-picker-small">
              <DateRangePicker
                value={value}
                onChange={(newValue) => setValue(newValue)}
                showShortcuts={true}
                classNames="w-fit"
              />
            </div>
          </div>
        </div>
        <Card extra={"w-full pb-6 relative"}>
          <div className="mt-2 mx-2">
            <div className="overflow-x-auto !p-0 !m-0 !pr-0" style={{ minWidth: '100%' }}>
              <table className="table-fixed border-collapse !mr-0 !pr-0 !border-r-0" style={{ tableLayout: 'fixed', width: '100%', minWidth: '1200px' }}>
              <thead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr
                    key={headerGroup.id}
                    className="!border-px !border-gray-400"
                  >
                    {headerGroup.headers.map((header, index) => {
                      const isFirst = index === 0;
                      const isLast = index === headerGroup.headers.length - 1;
                      const columnId = header.column.id;
                      
                      // Set fixed widths for all columns to prevent layout shift
                      let columnWidth = 'auto';
                      if (columnId === 'full_name') columnWidth = '180px';
                      else if (columnId === 'account_type') columnWidth = '130px';
                      else if (columnId === 'is_active') columnWidth = '100px';
                      else if (columnId === 'email') columnWidth = '200px';
                      else if (columnId === 'coin_balance') columnWidth = '180px';
                      else if (columnId === 'action') columnWidth = '150px';
                      
                      return (
                        <th
                          key={header.id}
                          colSpan={header.colSpan}
                          onClick={header.column.getToggleSortingHandler()}
                          className={`cursor-pointer border-b-[1px] border-gray-500/10 pb-2 pt-4 text-start ${isFirst ? 'pl-6' : 'pl-4'} ${isLast ? '' : 'pr-4'}`}
                          style={{ width: columnWidth }}
                        >
                          <div className="items-center justify-between text-xs text-gray-200">
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                            {{
                              asc: "",
                              desc: "",
                            }[header.column.getIsSorted()] ?? null}
                          </div>
                        </th>
                      );
                    })}
                  </tr>
                ))}
              </thead>
              <tbody>
                {isLoading && tabCache["account-verification"]?.firstLoad && !tabCache["account-verification"]?.data ? (
                  <tr>
                    <td colSpan={columns.length} className="h-[600px] align-middle">
                      <div className="flex h-full items-center justify-center">
                        <img
                          src={Loader}
                          alt="Loading..."
                          className="h-16 w-16"
                        />
                      </div>
                    </td>
                  </tr>
                ) : table.getRowModel().rows?.length === 0 && !isLoading ? (
                  <tr>
                    <td colSpan={columns.length} className="py-12 align-middle">
                      <div className="flex items-center justify-center">
                        <p className="text-white">No data found</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  <>
                    {table.getRowModel().rows.map((row, rowIndex) => {
                      const isLastRow = rowIndex === table.getRowModel().rows.length - 1;
                      return (
                        <tr key={row.id} className={isLastRow ? "" : "border-b-[1px] border-gray-500/10"}>
                          {row.getVisibleCells().map((cell, cellIndex) => {
                            const isFirst = cellIndex === 0;
                            const isLast = cellIndex === row.getVisibleCells().length - 1;
                            const columnId = cell.column.id;
                            
                            // Set fixed widths matching header widths
                            let columnWidth = 'auto';
                            if (columnId === 'full_name') columnWidth = '200px';
                            else if (columnId === 'account_type') columnWidth = '150px';
                            else if (columnId === 'is_active') columnWidth = '120px';
                            else if (columnId === 'email') columnWidth = '220px';
                            else if (columnId === 'coin_balance') columnWidth = '150px';
                            else if (columnId === 'action') columnWidth = '250px';
                            
                            // Check if this is the Action column (last column)
                            const isActionColumn = isLast;
                            
                            return (
                              <td
                                key={cell.id}
                                className={`border-white/0 py-3 ${isFirst ? 'pl-6' : isActionColumn ? 'pl-1' : 'pl-4'} ${isLast ? '' : 'pr-4'}`}
                                style={{ width: columnWidth }}
                              >
                                {flexRender(
                                  cell.column.columnDef.cell,
                                  cell.getContext()
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </>
                )}
              </tbody>
            </table>
            </div>
          </div>

          {/* Google-style Pagination */}
          {!isLoading && table.getRowModel().rows.length > 0 && totalPages > 1 && (
            <div className="mt-3 pt-3 px-6">
              <GooglePagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalCount={totalCount}
                pageSize={pageSize}
                onPageChange={handlePageChange}
                className="w-full"
              />
            </div>
          )}
        </Card>

        <Modal
          isOpen={popupOpen}
          onClose={() => {
            setPopupOpen(false);
            document.body.style.overflow = "unset";
          }}
          isActive={selectedUser}
          onToggle={handleToggleActivation}
          activateRef={activateRef}
          confirmLoader={confirmLoader}
        />
      </div>
    </div>
  );
};

export default AccountVerification;
